import React, { useState, useEffect } from 'react';
import { X, MapPin, Camera, Loader2, ChevronDown, ChevronRight, Link2, AlertTriangle, Info } from 'lucide-react';
import { postsApi, PostCategory, Post } from '../services/postsApi';
import { GeoLocation } from '../types';

const CATEGORY_LABELS: Array<[PostCategory, string]> = [
  ['police_activity', 'Police Activity'],
  ['shooting',        'Shooting / Gunfire'],
  ['fire_ems',        'Fire / EMS'],
  ['traffic',         'Traffic'],
  ['pursuit',         'Pursuit'],
  ['containment',     'Containment'],
  ['suspicious',      'Suspicious'],
  ['community',       'Community'],
  ['other',           'Other'],
];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (post: Post) => void;
  userLocation?: GeoLocation | null;
  userRole?: 'user' | 'trusted_source' | 'admin';
}

const MAX_TITLE = 120;
const MAX_DESC  = 4000;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_ATTR_LABEL = 200;

type ModerationOutcome = { flagged: boolean; tier: number | null; hidden: boolean };

function isValidHttpUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export default function PostComposer({ open, onClose, onCreated, userLocation, userRole }: Props) {
  const [category, setCategory] = useState<PostCategory>('police_activity');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [attachLocation, setAttachLocation] = useState(true);
  const [media, setMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ModerationOutcome | null>(null);

  // Trusted Source attribution fields
  const [attributionOpen, setAttributionOpen] = useState(false);
  const [attributionUrl, setAttributionUrl] = useState('');
  const [attributionLabel, setAttributionLabel] = useState('');

  const isTrustedSource = userRole === 'trusted_source';

  useEffect(() => {
    if (!open) {
      setCategory('police_activity'); setTitle(''); setDescription('');
      setMedia(null); setMediaPreview(null); setSubmitting(false); setError(null);
      setOutcome(null);
      setAttributionOpen(false); setAttributionUrl(''); setAttributionLabel('');
    }
  }, [open]);

  useEffect(() => {
    if (!media) { setMediaPreview(null); return; }
    const url = URL.createObjectURL(media);
    setMediaPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [media]);

  if (!open) return null;

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      setError(`Image too large (max ${Math.round(MAX_FILE_BYTES/1024/1024)}MB).`);
      return;
    }
    setError(null);
    setMedia(f);
  };

  const submit = async () => {
    setError(null);
    if (title.trim().length < 3) { setError('Title is too short.'); return; }
    if (description.trim().length < 10) { setError('Description is too short.'); return; }

    // Validate attribution fields if Trusted Source provided them
    let attrUrl: string | undefined;
    let attrLabel: string | undefined;
    if (isTrustedSource) {
      const u = attributionUrl.trim();
      const l = attributionLabel.trim();
      if (u) {
        if (!isValidHttpUrl(u)) { setError('Attribution URL must be a valid http(s) URL.'); return; }
        attrUrl = u;
      }
      if (l) {
        if (l.length > MAX_ATTR_LABEL) { setError(`Attribution label is too long (max ${MAX_ATTR_LABEL}).`); return; }
        attrLabel = l;
      }
    }

    setSubmitting(true);
    try {
      const result = await postsApi.create({
        category,
        title: title.trim(),
        description: description.trim(),
        lat: attachLocation && userLocation ? userLocation.lat : undefined,
        lng: attachLocation && userLocation ? userLocation.lng : undefined,
        media,
        attributionUrl: attrUrl,
        attributionLabel: attrLabel,
      });
      onCreated(result.post);
      const mod = result.moderation;
      if (mod.hidden || mod.flagged) {
        setOutcome(mod);
        // Keep modal open so the user sees the banner; they dismiss with Close.
      } else {
        onClose();
      }
    } catch (e: any) {
      setError(e?.message || 'Could not post.');
    } finally {
      setSubmitting(false);
    }
  };

  // If we have an outcome to show, render a results view inside the same modal frame.
  const renderOutcomeView = () => {
    if (!outcome) return null;
    if (outcome.hidden) {
      return (
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-2 text-amber-200 bg-amber-900/30 border border-amber-700/60 rounded px-3 py-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-bold">Your post is under review.</div>
              <div className="text-amber-200/80 mt-1">
                It will appear once a moderator approves it.
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-full px-3 py-2 rounded bg-amber-600 hover:bg-amber-500 text-slate-950 font-bold"
          >
            Close
          </button>
        </div>
      );
    }
    // flagged but live
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-2 text-sky-200 bg-sky-900/30 border border-sky-700/60 rounded px-3 py-3">
          <Info size={18} className="mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-bold">Posted — flagged for review.</div>
            <div className="text-sky-200/80 mt-1">
              Your post is live but flagged for review.
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-full px-3 py-2 rounded bg-sky-600 hover:bg-sky-500 text-slate-950 font-bold"
        >
          Close
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-900 border border-amber-700/40 rounded-lg w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-slate-800">
          <h2 className="text-amber-200 font-bold tracking-wide">
            {outcome ? 'POST SUBMITTED' : 'REPORT AN INCIDENT'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200" aria-label="Close"><X size={18}/></button>
        </header>

        {outcome ? renderOutcomeView() : (
          <div className="p-4 space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-400">Category</span>
              <select value={category} onChange={e => setCategory(e.target.value as PostCategory)}
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100">
                {CATEGORY_LABELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-400">Title</span>
              <input value={title} onChange={e => setTitle(e.target.value.slice(0, MAX_TITLE))}
                placeholder="Short summary"
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100" />
              <span className="text-[10px] text-slate-500">{title.length}/{MAX_TITLE}</span>
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-400">Description</span>
              <textarea value={description} onChange={e => setDescription(e.target.value.slice(0, MAX_DESC))}
                rows={5}
                placeholder="What happened? Where? When?"
                className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100 resize-y" />
              <span className="text-[10px] text-slate-500">{description.length}/{MAX_DESC}</span>
            </label>

            <label className={`flex items-center gap-2 text-sm cursor-pointer ${userLocation ? 'text-slate-300' : 'text-slate-500'}`}>
              <input type="checkbox" checked={attachLocation && !!userLocation} disabled={!userLocation}
                onChange={e => setAttachLocation(e.target.checked)} />
              <MapPin size={14} />
              {userLocation ? 'Attach my current location to this post' : 'Location unavailable — enable GPS to attach a pin'}
            </label>

            <div>
              <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-slate-200 cursor-pointer hover:bg-slate-700">
                <Camera size={14} /> {media ? 'Change photo' : 'Add photo (optional)'}
                <input type="file" accept="image/*" className="hidden" onChange={onFile} />
              </label>
              {mediaPreview && (
                <div className="mt-2">
                  <img src={mediaPreview} className="rounded border border-slate-700 max-h-48" alt="preview" />
                  <button onClick={() => setMedia(null)} className="text-xs text-slate-400 hover:text-slate-200 mt-1">remove</button>
                </div>
              )}
            </div>

            {isTrustedSource && (
              <div className="border border-slate-700 rounded">
                <button
                  type="button"
                  onClick={() => setAttributionOpen(o => !o)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs uppercase tracking-wide text-amber-200/90 hover:bg-slate-800"
                >
                  {attributionOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Link2 size={14} />
                  Cross-post attribution (optional)
                </button>
                {attributionOpen && (
                  <div className="p-3 space-y-2 border-t border-slate-700">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">URL</span>
                      <input
                        type="url"
                        inputMode="url"
                        value={attributionUrl}
                        onChange={e => setAttributionUrl(e.target.value)}
                        placeholder="https://example.com/original-source"
                        className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">Label</span>
                      <input
                        type="text"
                        value={attributionLabel}
                        onChange={e => setAttributionLabel(e.target.value.slice(0, MAX_ATTR_LABEL))}
                        placeholder="e.g. KOLD News 13 — Original Report"
                        className="mt-1 w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-slate-100 text-sm"
                      />
                      <span className="text-[10px] text-slate-500">{attributionLabel.length}/{MAX_ATTR_LABEL}</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {error && <div className="text-xs text-red-300 bg-red-900/30 border border-red-800 rounded px-2 py-1">{error}</div>}

            <div className="flex gap-2 pt-1">
              <button onClick={onClose} className="flex-1 px-3 py-2 rounded border border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</button>
              <button onClick={submit} disabled={submitting}
                className="flex-1 px-3 py-2 rounded bg-amber-600 hover:bg-amber-500 text-slate-950 font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {submitting && <Loader2 size={14} className="animate-spin" />} Post
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
